import React from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../../constants/Colors";
import StepWrapper from "./StepWrapper";
import {
  CreateFlatStore,
  Gender,
  MIN_FLAT_MEMBERS,
  MAX_FLAT_MEMBERS,
  MIN_AGE,
  MAX_AGE,
} from "../../hooks/useCreateFlatStore";

interface Props {
  store: CreateFlatStore;
}

const MembersStep: React.FC<Props> = ({ store }) => {
  const { t } = useTranslation();
  const {
    containerNeutralLightMedium,
    textNeutralDefault,
    accentColor,
    textNeutralMuted,
  } = useColors();

  const genderOptions: { key: Gender; label: string }[] = [
    { key: "male", label: t("male") || "Male" },
    { key: "female", label: t("female") || "Female" },
    { key: "non_binary", label: t("non_binary") || "Non-binary" },
  ];

  const toggleGender = (gender: Gender) => {
    if (store.lookingFor.includes(gender)) {
      store.setLookingFor(store.lookingFor.filter((g) => g !== gender));
    } else {
      store.setLookingFor([...store.lookingFor, gender]);
    }
  };

  const handleMemberInput = (value: string) => {
    const cleaned = value.replace(/[^0-9]/g, "");
    store.setMemberCount(cleaned);
  };

  const handleAgeInput = (value: string, isMin: boolean) => {
    const cleaned = value.replace(/[^0-9]/g, "");
    if (isMin) {
      store.setLookingForAgeRange([
        cleaned === "" ? -1 : parseInt(cleaned),
        store.lookingForAgeRange[1],
      ]);
    } else {
      store.setLookingForAgeRange([
        store.lookingForAgeRange[0],
        cleaned === "" ? -1 : parseInt(cleaned),
      ]);
    }
  };

  const displayAge = (age: number) => (age === -1 ? "" : age.toString());

  return (
    <StepWrapper
      title={
        t("flats_tab.add_flat.flat_looking_for_title") ||
        "Who are you looking for?"
      }
    >
      <div className="space-y-8">
        {/* Gender selection */}
        <div className="flex flex-wrap justify-center gap-3">
          {genderOptions.map((option) => {
            const isSelected = store.lookingFor.includes(option.key);
            return (
              <button
                key={option.key}
                onClick={() => toggleGender(option.key)}
                className="cf-pill"
                style={{
                  backgroundColor: isSelected
                    ? accentColor
                    : "rgba(2, 2, 16, 0.05)",
                  color: isSelected ? "white" : textNeutralDefault,
                  borderColor: isSelected
                    ? accentColor
                    : "rgba(2, 2, 16, 0.08)",
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {/* Age range */}
        <div className="flex justify-center items-center gap-3 pt-1">
          <input
            type="text"
            inputMode="numeric"
            value={displayAge(store.lookingForAgeRange[0])}
            onChange={(e) => handleAgeInput(e.target.value, true)}
            className="cf-number-input"
            style={{ color: textNeutralDefault }}
            placeholder={String(MIN_AGE)}
          />
          <span
            className="text-lg font-light"
            style={{ color: textNeutralMuted }}
          >
            &ndash;
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={displayAge(store.lookingForAgeRange[1])}
            onChange={(e) => handleAgeInput(e.target.value, false)}
            className="cf-number-input"
            style={{ color: textNeutralDefault }}
            placeholder={String(MAX_AGE)}
          />
          <span className="text-sm ml-1" style={{ color: textNeutralMuted }}>
            {t("years_old") || "years old"}
          </span>
        </div>

        {/* Member count */}
        <div className="pt-4 text-center">
          <h3
            className="text-xl font-gnarly-semibold mb-5"
            style={{ color: textNeutralDefault }}
          >
            {t("flats_tab.add_flat.flat_member_count_title") ||
              "How many people live in your flat?"}
          </h3>
          <input
            type="text"
            inputMode="numeric"
            value={store.memberCount}
            onChange={(e) => handleMemberInput(e.target.value)}
            placeholder="2"
            className="cf-number-input"
            style={{ color: textNeutralDefault }}
          />
        </div>
      </div>
    </StepWrapper>
  );
};

export default MembersStep;
